import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  CheckIcon,
  Loader,
  Skeleton,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconInfoCircle,
} from "@tabler/icons-react";
import {
  danger,
  expandedBg,
  gray,
  info,
  primary,
  success,
  warning,
} from "../../constants/color-constants";
import { DATE_FORMAT } from "../../constants/app-constant";
import { useAppSelector } from "../../store/hooks";
import { CDIPreQueryAlert, CDIPreQueryClinicalIndicator } from "../../types/cdi-alerts";
import {
  formatDate,
  formatTimeWithZoneAbbreviation,
} from "../../utils";
import { normalizeCDIAlertStatus } from "../../utils/cdi-alert-utils";
import { getSeriesNumberColumnStyle, formatSeriesNumber } from "../../utils/series-number-column";
import CheckBoxIcon from "../../assets/AlertCheckBox.svg";
import ActiveCheckBoxIcon from "../../assets/AlertCheckBoxActive.svg";
import PendingIcon from "../../assets/PendingIcon.svg";

type V2SectionCardProps = {
  accentColor: string;
  title: string;
  items: CDIPreQueryClinicalIndicator[];
};

type CDIAlertsV2TableProps = {
  alerts: CDIPreQueryAlert[];
  loading?: boolean;
  onAcknowledge?: (alertId: string) => void;
};

const ALERT_TYPE_LABELS: Record<string, string> = {
  DCC: "Diagnosis Capture Consideration",
  ACV: "Alert for Clinical Validation",
  AI: "Abnormal Imaging",
  AL: "Abnormal Lab",
};

const formatShortChartDate = (value?: string) => {
  if (!value) return "";

  const parts = value.split("-");
  if (parts.length === 3) {
    return `${parts[0]}-${parts[1]}`;
  }

  return value;
};

const getSeverityStyles = (severity: string) => {
  switch (severity) {
    case "HIGH":
      return {
        color: danger[4],
        backgroundColor: danger[0],
        border: danger[2],
      };
    case "MEDIUM":
      return {
        color: warning[4],
        backgroundColor: warning[0],
        border: warning[2],
      };
    default:
      return {
        color: success[6],
        backgroundColor: success[0],
        border: success[2],
      };
  }
};

const getTypeStyles = (type: string) => {
  switch (type) {
    case "DCC":
      return {
        color: success[6],
        backgroundColor: success[0],
        border: success[2],
      };
    case "ACV":
      return {
        color: primary[5],
        backgroundColor: primary[0],
        border: primary[2],
      };
    case "AI":
      return {
        color: info[4],
        backgroundColor: info[0],
        border: info[2],
      };
    default:
      return {
        color: warning[4],
        backgroundColor: warning[0],
        border: warning[2],
      };
  }
};

const buildIndicatorTitle = (indicator: CDIPreQueryClinicalIndicator) => {
  if (indicator.name && indicator.result) {
    return `${indicator.name}: ${indicator.result}`;
  }

  if (indicator.name) {
    return indicator.name;
  }

  if (indicator.result) {
    return indicator.result;
  }

  if (indicator.text) {
    return indicator.text;
  }

  return "Clinical Indicator";
};

const buildIndicatorBody = (indicator: CDIPreQueryClinicalIndicator) => {
  if (indicator.text) {
    return indicator.text;
  }

  return indicator.evidences?.[0]?.originalText || indicator.evidences?.[0]?.text || "";
};

const buildValidationFindingText = (finding?: string, detail?: string) => {
  if (finding) return finding;
  if (detail) return detail;

  return "--";
};

const buildValidationDetailText = (detail?: string, evidenceText?: string) => {
  if (detail) return detail;
  if (evidenceText) return evidenceText;

  return "--";
};

const buildValidationReference = (source?: string, date?: string) => {
  const refParts = [source, formatShortChartDate(date)].filter(Boolean);

  if (refParts.length === 0) {
    return "";
  }

  return `REF: ${refParts.join(" ")}`;
};

const getRowBackground = (index: number) =>
  index % 2 === 0 ? "#FFFFFF" : gray[0];

const getExpandedRowBackground = () => expandedBg;

const renderActionStatus = (
  status: string,
  rowBackgroundColor: string,
  options?: {
    isAcknowledging?: boolean;
    onAcknowledge?: () => void;
  }
) => {
  const { isAcknowledging = false, onAcknowledge } = options || {};

  if (status === "PENDING") {
    return (
      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Box
          component='img'
          src={isAcknowledging ? ActiveCheckBoxIcon : CheckBoxIcon}
          mb={4}
          style={{
            cursor: isAcknowledging ? "default" : "pointer",
            backgroundColor: rowBackgroundColor,
          }}
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
            if (!isAcknowledging) {
              onAcknowledge?.();
            }
          }}
        />
        <Badge
          color={info[4]}
          variant='light'
          radius='sm'
          size='xs'
          style={{
            border: `1px solid ${info[4]}`,
            height: "20px",
            lineHeight: "22px",
            padding: "0px 8px",
            minWidth: "75px",
            maxWidth: "120px",
            width: "fit-content",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            textTransform: "none",
          }}
        >
          {isAcknowledging ? (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                gap: 4,
                minWidth: "75px",
                height: "100%",
              }}
            >
              <Loader size={12} color={primary[5]} />
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                gap: 4,
                fontSize: "var(--mantine-font-size-xs)",
                minWidth: "75px",
              }}
            >
              <Box component='img' src={PendingIcon} mb={1} />
              Pending
            </div>
          )}
        </Badge>
      </Box>
    );
  }

  if (status === "ACKNOWLEDGED") {
    return (
      <Badge
        color={success[6]}
        variant='light'
        radius='sm'
        size='xs'
        style={{
          border: `1px solid ${success[6]}`,
          height: "20px",
          lineHeight: "22px",
          padding: "0px 8px",
          minWidth: "75px",
          maxWidth: "120px",
          width: "fit-content",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          textTransform: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: 4,
            fontSize: "var(--mantine-font-size-xs)",
          }}
        >
          <CheckIcon
            color={success[6]}
            style={{
              height: "20px",
              width: "10px",
            }}
          />
          Acknowledged
        </div>
      </Badge>
    );
  }

  return "-";
};

const V2SectionCard: React.FC<V2SectionCardProps> = ({
  accentColor,
  title,
  items,
}) => (
  <Box
    style={{
      backgroundColor: "#FFFFFF",
      border: `1px solid ${primary[3]}`,
      borderRadius: 0,
      overflow: "hidden",
    }}
  >
    <Box
      px='sm'
      py='xs'
      style={{
        borderLeft: `4px solid ${accentColor}`,
        backgroundColor: expandedBg,
        borderBottom: `1px solid ${primary[3]}`,
      }}
    >
      <Text size='sm' fw={700} c={gray[9]} style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>{title}</Text>
    </Box>

    {items.length > 0 ? (
      items.map((item, index) => {
        const body = buildIndicatorBody(item);
        const meta = [item.source, formatShortChartDate(item.date)]
          .filter(Boolean)
          .join(" | ");

        return (
          <Box
            key={`${item.name}-${item.date}-${index}`}
            px='sm'
            py='xs'
            style={{
              borderTop: index === 0 ? "none" : `1px solid ${gray[2]}`,
              minHeight: "68px",
            }}
          >
            <Text
              fw={700}
              size='sm'
              c={gray[9]}
              style={{ lineHeight: 1.35, wordBreak: "break-word" }}
            >
              {buildIndicatorTitle(item)}
            </Text>

            {body ? (
              <Text
                mt={2}
                size='sm'
                fw={400}
                c={gray[8]}
                style={{
                  lineHeight: 1.4,
                  wordBreak: "break-word",
                  fontStyle: item.result ? "normal" : "italic",
                }}
              >
                {item.result ? body : `"${body}"`}
              </Text>
            ) : null}

            {meta ? (
              <Text
                mt={4}
                size='sm'
                fw={400}
                c={gray[8]}
                style={{
                  letterSpacing: "0.03em",
                  textTransform: "uppercase",
                  wordBreak: "break-word",
                  fontStyle: "italic",
                }}
              >
                {meta}
              </Text>
            ) : null}
          </Box>
        );
      })
    ) : (
      <Box px='sm' py='sm'>
        <Text size='sm' c={gray[8]}>
          No supporting items available.
        </Text>
      </Box>
    )}
  </Box>
);

const ACVValidationTable: React.FC<{
  alert: CDIPreQueryAlert;
  backgroundColor: string;
}> = ({ alert, backgroundColor }) => (
  <Box
    px='xs'
    py='xs'
    style={{
      backgroundColor,
    }}
  >
    <Text
      size='sm'
      fw={700}
      c={gray[9]}
      style={{ letterSpacing: "0.04em", textTransform: "uppercase" }}
    >
      Physician Validation Deficiencies
    </Text>

    <Box
      mt='xs'
      style={{
        border: `1px solid ${primary[3]}`,
        backgroundColor: "#FFFFFF",
        borderRadius: 0,
        overflow: "hidden",
      }}
    >
      <Box
        style={{
          display: "grid",
          gridTemplateColumns: "1.15fr 1fr 2fr",
          backgroundColor: expandedBg,
          borderBottom: `1px solid ${primary[3]}`,
        }}
      >
        <Box px='md' py='sm'>
          <Text
            size='sm'
            fw={700}
            c={gray[9]}
            style={{ textTransform: "uppercase", letterSpacing: "0.02em" }}
          >
            Criteria Requirement
          </Text>
        </Box>
        <Box px='md' py='sm'>
          <Text
            size='sm'
            fw={700}
            c={gray[9]}
            style={{ textTransform: "uppercase", letterSpacing: "0.02em" }}
          >
            Current Finding
          </Text>
        </Box>
        <Box px='md' py='sm'>
          <Text
            size='sm'
            fw={700}
            c={gray[9]}
            style={{ textTransform: "uppercase", letterSpacing: "0.02em" }}
          >
            Deficiency Detail & Evidence
          </Text>
        </Box>
      </Box>

      {alert.validationSummary.map((item, index) => {
        const evidenceText =
          item.evidences?.[0]?.text || item.evidences?.[0]?.originalText || "";
        const reference = buildValidationReference(item.source, item.date);

        return (
          <Box
            key={`${item.requirement}-${index}`}
            style={{
              display: "grid",
              gridTemplateColumns: "1.15fr 1fr 2fr",
              borderTop: index === 0 ? "none" : `1px solid ${gray[2]}`,
            }}
          >
            <Box px='sm' py='xs'>
              <Text size='sm' fw={600} c={gray[9]} style={{ lineHeight: 1.35 }}>
                {item.requirement || "--"}
              </Text>
            </Box>

            <Box px='sm' py='xs'>
              <Text
                size='sm'
                fw={700}
                c={item.status === "met" ? success[4] : danger[4]}
                style={{ lineHeight: 1.35 }}
              >
                {buildValidationFindingText(item.finding, item.detail)}
              </Text>
            </Box>

            <Box px='sm' py='xs'>
              <Text size='sm' fw={400} c={gray[8]} style={{ lineHeight: 1.35 }}>
                {buildValidationDetailText(item.detail, evidenceText)}
              </Text>

              {reference ? (
                <Text
                  mt={4}
                  size='sm'
                  fw={400}
                  c={gray[8]}
                  style={{ textTransform: "uppercase", letterSpacing: "0.03em", fontStyle: "italic" }}
                >
                  {reference}
                </Text>
              ) : null}
            </Box>
          </Box>
        );
      })}

      {alert.validationSummary.length === 0 ? (
        <Box px='sm' py='xs'>
          <Text size='sm' c={gray[8]}>
            No validation summary available.
          </Text>
        </Box>
      ) : null}
    </Box>
  </Box>
);

const CDIAlertsV2Table: React.FC<CDIAlertsV2TableProps> = ({
  alerts,
  loading,
  onAcknowledge,
}) => {
  const provider = useAppSelector((state) => state?.provider?.provider);
  const [expandedAlertId, setExpandedAlertId] = useState<string | null>(null);
  const [acknowledgingIds, setAcknowledgingIds] = useState<Set<string>>(
    () => new Set()
  );

  useEffect(() => {
    setExpandedAlertId(alerts?.[0]?.id || null);
  }, [alerts]);

  useEffect(() => {
    setAcknowledgingIds((current) => {
      if (current.size === 0) {
        return current;
      }

      const pendingIds = new Set(
        alerts
          .filter((alert) => normalizeCDIAlertStatus(alert.status) === "PENDING")
          .map((alert) => alert.id)
      );
      const next = new Set(
        Array.from(current).filter((alertId) => pendingIds.has(alertId))
      );
      return next.size === current.size ? current : next;
    });
  }, [alerts]);

  const rows = useMemo(
    () =>
      alerts.map((alert) => {
        const signsAndSymptoms = alert.clinicalIndicators.filter(
          (indicator) => indicator.type === "SSV"
        );
        const labAndImageFindings = alert.clinicalIndicators.filter(
          (indicator) => indicator.type === "LIF" || indicator.type === "ALR"
        );
        const treatments = alert.clinicalIndicators.filter(
          (indicator) => indicator.type === "TM"
        );

        return {
          ...alert,
          signsAndSymptoms,
          labAndImageFindings,
          treatments,
        };
      }),
    [alerts]
  );

  const seriesColStyle = useMemo(
    () => getSeriesNumberColumnStyle(rows.length),
    [rows.length]
  );

  return (
    <Box
      style={{
        overflowX: "auto",
        overflowY: "auto",
        flex: 1,
        backgroundColor: gray[0],
      }}
    >
      <Table
        highlightOnHover={!loading}
        highlightOnHoverColor={gray[0]}
        style={{
          borderCollapse: "separate",
          borderSpacing: 0,
          minWidth: "700px",
        }}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th
              style={{
                ...seriesColStyle,
                backgroundColor: gray[2],
                padding: "6px 4px",
                borderBottom: "2px solid #dee2e6",
                fontSize: "14px",
                fontWeight: 600,
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                position: "sticky",
                top: 0,
                zIndex: 500,
              }}
            >
              #
            </Table.Th>
            <Table.Th
              style={{
                backgroundColor: gray[2],
                padding: "6px 16px",
                width: "auto",
                borderBottom: "2px solid #dee2e6",
                fontSize: "14px",
                fontWeight: 600,
                textAlign: "left",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                position: "sticky",
                top: 0,
                zIndex: 500,
              }}
            >
              Alerts
            </Table.Th>
            <Table.Th
              style={{
                backgroundColor: gray[2],
                padding: "6px 16px",
                width: "170px",
                borderBottom: "2px solid #dee2e6",
                fontSize: "14px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                textAlign: "left",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                position: "sticky",
                top: 0,
                zIndex: 500,
              }}
            >
              Action/Status
            </Table.Th>
            <Table.Th
              style={{
                backgroundColor: gray[2],
                padding: "6px 16px",
                width: "230px",
                borderBottom: "2px solid #dee2e6",
                fontSize: "14px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                textAlign: "left",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                position: "sticky",
                top: 0,
                zIndex: 500,
              }}
            >
              Date/Time
            </Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {loading
            ? Array.from({ length: 3 }).map((_, index) => (
                <Table.Tr key={`skeleton-${index}`}>
                  <Table.Td colSpan={4} style={{ padding: "18px 16px" }}>
                    <Skeleton height={40} radius='md' mb={10} />
                    <Skeleton height={140} radius='md' />
                  </Table.Td>
                </Table.Tr>
              ))
            : rows.map((alert, index) => {
                const severityStyles = getSeverityStyles(alert.severity);
                const typeStyles = getTypeStyles(alert.type);
                const status = normalizeCDIAlertStatus(alert.status);
                const isExpanded = expandedAlertId === alert.id;
                const baseRowBackgroundColor = getRowBackground(index);
                const expandedRowBackgroundColor = getExpandedRowBackground();
                const rowBackgroundColor = isExpanded
                  ? expandedRowBackgroundColor
                  : baseRowBackgroundColor;
                const dateText = formatDate(
                  alert.timestamp,
                  DATE_FORMAT.MM_DD_YYYY
                );
                const timeText = formatTimeWithZoneAbbreviation(
                  alert.timestamp,
                  provider?.timezone_info?.timezone_name,
                  provider?.timezone_info?.abbrev_simple
                );

                return (
                  <React.Fragment key={alert.id}>
                    <Table.Tr
                      onClick={() =>
                        setExpandedAlertId((current) =>
                          current === alert.id ? null : alert.id
                        )
                      }
                      style={{
                        backgroundColor: rowBackgroundColor,
                        cursor: "pointer",
                      }}
                    >
                      <Table.Td
                        style={{
                          ...seriesColStyle,
                          borderBottom: isExpanded
                            ? `1px solid ${primary[3]}`
                            : `1px solid ${gray[2]}`,
                          padding: "10px 4px",
                          backgroundColor: rowBackgroundColor,
                          verticalAlign: "top",
                        }}
                      >
                        <Text
                          fw={500}
                          size='sm'
                          c={gray[8]}
                          ta='center'
                          style={{ lineHeight: 1.35 }}
                        >
                          {formatSeriesNumber(index + 1)}
                        </Text>
                      </Table.Td>

                      <Table.Td
                        style={{
                          borderBottom: isExpanded
                            ? `1px solid ${primary[3]}`
                            : `1px solid ${gray[2]}`,
                          padding: "10px 16px",
                          backgroundColor: rowBackgroundColor,
                        }}
                      >
                        <Box style={{ minWidth: 0 }}>
                            <Text
                              fw={700}
                              size='sm'
                              c={gray[9]}
                              style={{ lineHeight: 1.35, wordBreak: "break-word", textTransform: "uppercase", letterSpacing: "0.04em" }}
                            >
                              {alert.title}
                            </Text>

                            <Box
                              mt={4}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                flexWrap: "wrap",
                              }}
                            >
                              <Badge
                                radius='sm'
                                size='xs'
                                variant='light'
                                style={{
                                  color: severityStyles.color,
                                  backgroundColor: severityStyles.backgroundColor,
                                  border: `1px solid ${severityStyles.border}`,
                                  textTransform: "none",
                                  fontWeight: 700,
                                  height: "20px",
                                }}
                              >
                                {alert.severity}
                              </Badge>

                              <Tooltip
                                label={ALERT_TYPE_LABELS[alert.type] || alert.type}
                                withArrow
                                position='bottom'
                                color={gray[8]}
                              >
                                <Badge
                                  radius='xl'
                                  size='xs'
                                  variant='light'
                                  style={{
                                    color: typeStyles.color,
                                    backgroundColor: typeStyles.backgroundColor,
                                    border: `1px solid ${typeStyles.border}`,
                                    textTransform: "none",
                                    fontWeight: 700,
                                    height: "20px",
                                  }}
                                >
                                  {alert.type}
                                </Badge>
                              </Tooltip>
                            </Box>
                          </Box>
                      </Table.Td>

                      <Table.Td
                        style={{
                          borderBottom: isExpanded
                            ? `1px solid ${primary[3]}`
                            : `1px solid ${gray[2]}`,
                          padding: "10px 16px",
                          verticalAlign: "middle",
                          backgroundColor: rowBackgroundColor,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {renderActionStatus(status, rowBackgroundColor, {
                          isAcknowledging: acknowledgingIds.has(alert.id),
                          onAcknowledge: () => {
                            setAcknowledgingIds((current) => {
                              const next = new Set(current);
                              next.add(alert.id);
                              return next;
                            });
                            onAcknowledge?.(alert.id);
                          },
                        })}
                      </Table.Td>

                      <Table.Td
                        style={{
                          borderBottom: isExpanded
                            ? `1px solid ${primary[3]}`
                            : `1px solid ${gray[2]}`,
                          padding: "10px 16px",
                          verticalAlign: "middle",
                          backgroundColor: rowBackgroundColor,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "10px",
                          }}
                        >
                          <Box style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <Text fw={500} size='xs' c={gray[8]}>
                              {[dateText, timeText].filter(Boolean).join(" ")}
                            </Text>
                            <Tooltip label={alert.id} withArrow position='left'>
                              <Box
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: primary[5],
                                }}
                              >
                                <IconInfoCircle size={16} />
                              </Box>
                            </Tooltip>
                          </Box>
                          <Box
                            style={{
                              display: "flex",
                              alignItems: "center",
                              color: primary[7],
                            }}
                          >
                            {isExpanded ? (
                              <IconChevronUp size={16} stroke={2.2} />
                            ) : (
                              <IconChevronDown size={16} stroke={2.2} />
                            )}
                          </Box>
                        </Box>
                      </Table.Td>
                    </Table.Tr>

                    {isExpanded ? (
                      <Table.Tr>
                        <Table.Td
                          colSpan={4}
                          style={{
                            padding: "6px 8px 8px",
                            backgroundColor: expandedBg,
                            borderTop: "2px solid #FFFFFF",
                            borderBottom: `1px solid ${primary[3]}`,
                          }}
                        >
                          {alert.type === "ACV" ? (
                            <ACVValidationTable
                              alert={alert}
                              backgroundColor={rowBackgroundColor}
                            />
                          ) : (
                            <>
                              <Box
                                style={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    "repeat(auto-fit, minmax(240px, 1fr))",
                                  gap: "8px",
                                  alignItems: "stretch",
                                }}
                              >
                                <V2SectionCard
                                  title='SIGNS AND SYMPTOMS/VITAL'
                                  accentColor={info[4]}
                                  items={alert.signsAndSymptoms}
                                />
                                <V2SectionCard
                                  title='LAB/IMAGING FINDINGS'
                                  accentColor={primary[5]}
                                  items={alert.labAndImageFindings}
                                />
                                <V2SectionCard
                                  title='TREATMENT/MANAGEMENT'
                                  accentColor={danger[4]}
                                  items={alert.treatments}
                                />
                              </Box>

                              <Text
                                mt='sm'
                                size='sm'
                                fw={700}
                                c={gray[9]}
                                style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
                              >
                                CLINICAL ASSESSMENT SUMMARY
                              </Text>
                              <Text
                                mt={4}
                                size='sm'
                                fw={400}
                                c={gray[8]}
                                style={{ lineHeight: 1.45 }}
                              >
                                {alert.issue ||
                                  "No clinical assessment summary available."}
                              </Text>

                              {alert.validationSummary?.length > 0 ? (
                                <Box mt='sm'>
                                  <Text
                                    fw={700}
                                    size='sm'
                                    c={gray[9]}
                                    style={{ letterSpacing: "0.04em", textTransform: "uppercase" }}
                                  >
                                    VALIDATION SUMMARY
                                  </Text>
                                  {alert.validationSummary.map((item, index) => (
                                    <Box
                                      key={`${item.requirement}-${index}`}
                                      mt='xs'
                                      px='sm'
                                      py='xs'
                                      style={{
                                        border: `1px solid ${gray[2]}`,
                                        borderRadius: 0,
                                        backgroundColor:
                                          item.status === "met"
                                            ? success[0]
                                            : danger[0],
                                      }}
                                    >
                                      <Text fw={700} size='sm' c={gray[9]}>
                                        {item.requirement}
                                      </Text>
                                      <Text mt={2} size='sm' fw={400} c={gray[8]}>
                                        {item.finding || item.detail}
                                      </Text>
                                    </Box>
                                  ))}
                                </Box>
                              ) : null}
                            </>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ) : null}
                  </React.Fragment>
                );
              })}

          {!loading && rows.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={4} style={{ padding: "28px 16px" }}>
                <Text ta='center' c={gray[8]} size='sm'>
                  No data available
                </Text>
              </Table.Td>
            </Table.Tr>
          ) : null}
        </Table.Tbody>
      </Table>
    </Box>
  );
};

export default CDIAlertsV2Table;
